import { useRef, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { Upload, Sparkles, X, ShieldCheck, BookOpen, ScrollText } from "lucide-react";
import { openBookPopout } from "../../lib/popout";

export default function GmStoryTab({ game, onGameUpdate, onSpawnToken }) {
  const fileRef = useRef(null);
  const [newName, setNewName] = useState("");
  const [pendingUrl, setPendingUrl] = useState(null);
  const isScopedGeneral = !!game.my_branch_id;

  const updateGame = async (updates) => {
    const { data } = await api.patch(`/games/${game.id}`, updates);
    onGameUpdate({ ...game, ...data });
  };

  const uploadPaletteImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post(`/uploads?game_id=${game.id}&kind=token`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    setPendingUrl(fileUrl(data.id));
  };

  const addPaletteEntry = () => {
    if (!newName.trim()) return toast.error("Give this token a name first");
    const entries = game.story_spawn_tokens || [];
    updateGame({ story_spawn_tokens: [...entries, { name: newName, image_url: pendingUrl }] });
    setNewName("");
    setPendingUrl(null);
  };

  const removePaletteEntry = (idx) => {
    const entries = (game.story_spawn_tokens || []).filter((_, i) => i !== idx);
    updateGame({ story_spawn_tokens: entries });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="open-story-book-button"
          onClick={() => openBookPopout(game.id, "story")}
          className="flex flex-col items-center gap-1.5 rounded-xl border border-[#D4AF37]/40 bg-gradient-to-b from-[#3a2412] to-[#241407] py-3 hover:border-[#D4AF37] transition-colors duration-200"
        >
          <BookOpen className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/80 font-display">Story Book</span>
        </button>
        <button
          data-testid="open-lore-book-button"
          onClick={() => openBookPopout(game.id, "lore")}
          className="flex flex-col items-center gap-1.5 rounded-xl border border-[#D4AF37]/40 bg-gradient-to-b from-[#3a2412] to-[#241407] py-3 hover:border-[#D4AF37] transition-colors duration-200"
        >
          <ScrollText className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/80 font-display">Lore Book</span>
        </button>
      </div>
      <p className="text-[10px] text-[#f1e0c5]/30 -mt-2">Opens in its own window — write your full campaign or world lore, chapter by chapter, with NPC reference cards you can click through as you play.</p>

      <div className="bg-black/20 rounded-lg p-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs text-[#f1e0c5]/70 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> GM Must Be Here</p>
          <p className="text-[10px] text-[#f1e0c5]/40 mt-0.5">
            {isScopedGeneral ? "Table-wide — only the Master GM can change this." : "Blocks player actions on Campaign scenes while you're offline. Free Roam always stays open."}
          </p>
        </div>
        <Switch data-testid="gm-require-present-switch" disabled={isScopedGeneral} checked={!!game.require_gm_present} onCheckedChange={(v) => updateGame({ require_gm_present: v })} />
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Story &amp; Plot Notes {isScopedGeneral && <span className="text-[#f1e0c5]/25">(Master GM's — read only)</span>}</p>
        <Textarea
          data-testid="gm-story-notes-textarea"
          value={game.story_notes || ""}
          onChange={(e) => !isScopedGeneral && updateGame({ story_notes: e.target.value })}
          readOnly={isScopedGeneral}
          placeholder="Write your plot beats, NPC hooks, and scene descriptions here. Add characters below and spawn them onto the map as they come up."
          className="bg-black/30 border-white/10 text-xs min-h-32"
        />
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Quick-Spawn Tokens</p>
        <p className="text-[10px] text-[#f1e0c5]/30 mb-2">Build a small cast of tokens from your notes above, then drop them onto the map the moment they show up.</p>
        <div className="space-y-1.5 mb-2">
          {(game.story_spawn_tokens || []).map((entry, idx) => (
            <div key={idx} data-testid={`story-spawn-entry-${idx}`} className="flex items-center gap-2 bg-black/20 rounded-lg px-2.5 py-1.5">
              {entry.image_url && <img src={entry.image_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
              <span className="flex-1 text-sm">{entry.name}</span>
              <button data-testid={`story-spawn-place-${idx}`} onClick={() => onSpawnToken({ name: entry.name, image_url: entry.image_url, token_type: "npc" })} className="text-xs px-2 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">Place on Map</button>
              {!isScopedGeneral && (
                <button data-testid={`story-spawn-remove-${idx}`} onClick={() => removePaletteEntry(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
        </div>
        {isScopedGeneral ? (
          <p className="text-[10px] text-[#f1e0c5]/25">Only the Master GM can add/remove entries here — "Place on Map" still works for your Branch.</p>
        ) : (
          <div className="flex gap-1.5">
            <Input data-testid="story-spawn-name-input" placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} className="bg-black/30 border-white/10 h-8 text-xs flex-1" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPaletteImage} data-testid="story-spawn-image-input" />
            <Button size="sm" variant="secondary" className="h-8 w-8 rounded-full p-0 shrink-0" onClick={() => fileRef.current?.click()} data-testid="story-spawn-upload-button">
              <Upload className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" className="h-8 rounded-full shrink-0" onClick={addPaletteEntry} data-testid="story-spawn-add-button">Add</Button>
          </div>
        )}
      </div>
    </div>
  );
}
